import { eventSlugFromPath } from './eventRegistrationIntent'
import { clearEventRegistrationIntent, readEventRegistrationIntent } from './webEventRegistrationIntent'
import { PAGE_ROUTES, REDIRECT_URL_KEY, UNAUTHENTICATED_REDIRECT_URL } from '../common/constants/routingConstants'

const isRegistrationOnboardingPath = (path: string) =>
  path.split(/[?#]/)[0].replace(/\/$/, '') === PAGE_ROUTES.REGISTRATION_ONBOARDING

const parseLoginRedirect = (value: string | null): unknown => {
  if (!value) return null
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

const clearMatchingAuthRedirects = (eventSlug: string, loginRedirect: unknown, signupRedirect: string | null) => {
  if (typeof loginRedirect === 'string' && eventSlugFromPath(loginRedirect) === eventSlug) {
    window.sessionStorage.removeItem(REDIRECT_URL_KEY)
  }
  if (signupRedirect && eventSlugFromPath(signupRedirect) === eventSlug) {
    window.sessionStorage.removeItem(UNAUTHENTICATED_REDIRECT_URL)
  }
}

/** Restore the saved event into the existing login/sign-up redirects before they choose a destination. */
export const restoreEventRegistrationAuthRedirects = async (eligible = true, registrationRedirect?: string) => {
  if (typeof window === 'undefined') return
  try {
    const storage = window.sessionStorage
    if (!storage) return
    const loginRaw = storage.getItem(REDIRECT_URL_KEY)
    const loginRedirect = parseLoginRedirect(loginRaw)
    const signupRedirect = storage.getItem(UNAUTHENTICATED_REDIRECT_URL)
    const intent = await readEventRegistrationIntent()
    if (!intent) return

    if (!eligible) {
      await clearEventRegistrationIntent(intent.intentId)
      clearMatchingAuthRedirects(intent.eventSlug, loginRedirect, signupRedirect)
      return
    }

    const path = window.location.pathname
    if (path.startsWith('/secure/index/') && eventSlugFromPath(path) === intent.eventSlug) {
      clearMatchingAuthRedirects(intent.eventSlug, loginRedirect, signupRedirect)
      return
    }

    // Respect another explicit destination rather than registering for an earlier choice.
    const destinations = [loginRedirect, signupRedirect].filter((value) => value !== null && value !== '')
    const hasConflictingRedirect = destinations.some(
      (value) => typeof value !== 'string' || eventSlugFromPath(value) !== intent.eventSlug,
    )
    const hasLeftAuthFlow =
      path.startsWith('/secure/') &&
      path !== PAGE_ROUTES.SECURE_INDEX &&
      path !== `${PAGE_ROUTES.SECURE_INDEX}/` &&
      !isRegistrationOnboardingPath(path) &&
      eventSlugFromPath(path) !== intent.eventSlug

    if (hasConflictingRedirect || hasLeftAuthFlow) {
      await clearEventRegistrationIntent(intent.intentId)
      return
    }

    // Registration uses its supplied destination when this tab has no sign-up redirect.
    // Recovering an older event here would change that existing routing priority.
    if (!signupRedirect && registrationRedirect !== undefined) {
      if (eventSlugFromPath(registrationRedirect) !== intent.eventSlug) {
        await clearEventRegistrationIntent(intent.intentId)
        clearMatchingAuthRedirects(intent.eventSlug, loginRedirect, signupRedirect)
      }
      return
    }

    if (!loginRaw) storage.setItem(REDIRECT_URL_KEY, JSON.stringify(intent.secureEventPath))
    if (!signupRedirect) storage.setItem(UNAUTHENTICATED_REDIRECT_URL, intent.secureEventPath)
  } catch {
    // Browser storage failure must not prevent authentication.
  }
}

/** Keep the selection through required onboarding; clear it when the user navigates elsewhere. */
export const abandonEventRegistrationForPath = async (path: string) => {
  const intent = await readEventRegistrationIntent()
  if (intent && !isRegistrationOnboardingPath(path) && eventSlugFromPath(path) !== intent.eventSlug) {
    await clearEventRegistrationIntent(intent.intentId)
  }
}
